import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "relative w-full h-full overflow-hidden bg-white" }

import {computed, onBeforeUnmount, reactive, ref, watch} from "vue";
import animationDefinitionsJson from "@/components/itemV3/itemV3ImageAnimationDefinitions.json";
import type {AnimationDirection, AnimationPoint, ImageAnimationDefinitions} from "@/components/itemV3/itemV3ImageAnimationTypes";

const DEFAULT_ANIMATION_ID = "ring-first-animation";

interface LayerStyle {
  backgroundImage: string;
  backgroundPosition: string;
  transformOrigin: string;
  opacity: number;
  transform: string;
  transition: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3ImageAnimationWeddingRings',
  props: {
    images: {},
    imageUrl: {},
    imagePosition: { default: "center" },
    animationId: { default: DEFAULT_ANIMATION_ID }
  },
  setup(__props: any) {

/**
 Shows one or more images in full width/height and animates them like a short,
 continuous "movie" about the piece of jewelry shown in the images.

 The animation itself is NOT hardcoded: it is fully controlled by a JSON animation
 definition (see `itemV3ImageAnimationDefinitions.json` / `itemV3ImageAnimationTypes.ts`)
 selected via the `animationId` prop. Each definition is a list of steps that are played
 one after another in a loop; every step picks one of the `images`, focuses on a point in
 it (x/y, used as the zoom/rotation origin), and animates scale, pan direction and rotation
 from a start to an end value over `speed` seconds. Between steps a "scene cut" transition
 is played: a soft cross-fade blend ("fade"), or an instant hard switch ("swap"/"cut").

 Two layers are used internally so the outgoing and incoming images/steps can overlap
 during a fade scene cut.
 */
const animationDefinitions = animationDefinitionsJson as ImageAnimationDefinitions;
const props = __props;

const resolvedImages = computed<string[]>(() => {
  if (props.images && props.images.length > 0) return props.images;
  if (props.imageUrl) return [props.imageUrl];
  return [];
});

const animationDefinition = computed(() => {
  const definition = animationDefinitions[props.animationId] ?? animationDefinitions[DEFAULT_ANIMATION_ID];
  return definition && definition.steps.length > 0 ? definition : null;
});

function createLayerStyle(): LayerStyle {
  return {
    backgroundImage: "none",
    backgroundPosition: "center",
    transformOrigin: "50% 50%",
    opacity: 0,
    transform: "scale(1) translate(0%, 0%) rotate(0deg)",
    transition: "none",
  };
}

const layerA = reactive<LayerStyle>(createLayerStyle());
const layerB = reactive<LayerStyle>(createLayerStyle());

const activeLayerName = ref<"a" | "b">("a");
let timers: number[] = [];

function clearTimers() {
  timers.forEach((timer) => window.clearTimeout(timer));
  timers = [];
}

function layerFor(name: "a" | "b"): LayerStyle {
  return name === "a" ? layerA : layerB;
}

function otherLayer(name: "a" | "b"): "a" | "b" {
  return name === "a" ? "b" : "a";
}

function directionPoint(direction: AnimationDirection | undefined, which: "start" | "end"): AnimationPoint {
  if (!direction || direction === "none") return {x: 0, y: 0};
  return direction[which];
}

function transformFor(scale: number, direction: AnimationPoint, rotation: number): string {
  return `scale(${scale}) translate(${direction.x}%, ${direction.y}%) rotate(${rotation}deg)`;
}

function imageForStep(stepImageIndex: number): string {
  const images = resolvedImages.value;
  if (images.length === 0) return "";
  return images[stepImageIndex] ?? images[0];
}

/**
 Plays the step at `stepIndex`. When `fadeInDuration` is given, the step's image/layer
 fades in (and the previous layer fades out) over that many seconds; otherwise it appears
 instantly (hard cut/swap).
 */
function playStep(stepIndex: number, fadeInDuration?: number) {
  const definition = animationDefinition.value;
  if (!definition || resolvedImages.value.length === 0) return;
  const steps = definition.steps;
  const step = steps[stepIndex % steps.length];
  const layer = layerFor(activeLayerName.value);
  const previousLayer = layerFor(otherLayer(activeLayerName.value));

  const startDirection = directionPoint(step.direction, "start");
  const endDirection = directionPoint(step.direction, "end");

  // Phase 1: place the layer in its starting state instantly, without any transition.
  layer.transition = "none";
  layer.backgroundImage = `url(${imageForStep(step.image)})`;
  layer.backgroundPosition = props.imagePosition;
  layer.transformOrigin = `${step.focus.x}% ${step.focus.y}%`;
  layer.transform = transformFor(step.scale.start, startDirection, step.rotation.start);

  if (fadeInDuration === undefined) {
    layer.opacity = 1;
    previousLayer.transition = "none";
    previousLayer.opacity = 0;
  } else {
    layer.opacity = 0;
  }

  // Phase 2: on the next frame, animate towards the end state over the step's "speed".
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const transitions = [`transform ${step.speed}s ease-in-out`];
      if (fadeInDuration !== undefined) {
        transitions.push(`opacity ${fadeInDuration}s ease-in-out`);
        previousLayer.transition = `opacity ${fadeInDuration}s ease-in-out`;
        previousLayer.opacity = 0;
      }
      layer.transition = transitions.join(", ");
      layer.transform = transformFor(step.scale.end, endDirection, step.rotation.end);
      layer.opacity = 1;
    });
  });

  const timer = window.setTimeout(() => cutToNextStep(stepIndex), step.speed * 1000);
  timers.push(timer);
}

function cutToNextStep(stepIndex: number) {
  const definition = animationDefinition.value;
  if (!definition) return;
  const steps = definition.steps;
  const step = steps[stepIndex % steps.length];
  const nextIndex = stepIndex + 1;
  if (nextIndex >= steps.length && definition.loop === false) return;

  const sceneCut = step.sceneCut ?? {type: "cut" as const};
  activeLayerName.value = otherLayer(activeLayerName.value);
  playStep(nextIndex, sceneCut.type === "fade" ? sceneCut.duration ?? 1 : undefined);
}

function start() {
  clearTimers();
  layerA.opacity = 0;
  layerB.opacity = 0;
  activeLayerName.value = "a";
  playStep(0);
}

watch(
    () => [props.animationId, resolvedImages.value.join("|")],
    () => start(),
    {immediate: true}
);

onBeforeUnmount(() => clearTimers());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "absolute inset-0 bg-no-repeat bg-cover",
      style: _normalizeStyle(layerA)
    }, null, 4),
    _createElementVNode("div", {
      class: "absolute inset-0 bg-no-repeat bg-cover",
      style: _normalizeStyle(layerB)
    }, null, 4)
  ]))
}
}

})